import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Bell,
  Settings,
  DollarSign,
  ChevronDown,
  Sun,
  Moon,
  LogOut,
  Coins,
  Building2,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../hooks/useAuth";
import { useTenant } from "../../context/TenantContext";
import { ROLE_DISPLAY_NAMES } from "../../constants/roles";

export default function TopNav({ title }) {
  const { darkMode, toggleDarkMode, currency, toggleCurrency } = useTheme();
  const { user, logout } = useAuth();
  const { currentTenant, tenants, switchTenant, canSwitchTenants } =
    useTenant();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showTenantMenu, setShowTenantMenu] = useState(false);
  const menuRef = useRef(null);
  const tenantMenuRef = useRef(null);

  // Token balance from user context (fetched from API)
  const tokenBalance = user?.token_balance || currentTenant?.token_balance || 0;

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (
        tenantMenuRef.current &&
        !tenantMenuRef.current.contains(event.target)
      ) {
        setShowTenantMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-6">
      {/* Left: Title & Tenant Switcher */}
      <div className="flex items-center gap-4">
        <h1 className="text-xl font-semibold text-gray-800 dark:text-white">
          {title}
        </h1>

        {/* Tenant Switcher (for platform admins) */}
        {canSwitchTenants && (
          <div className="relative" ref={tenantMenuRef}>
            <button
              onClick={() => setShowTenantMenu(!showTenantMenu)}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700 dark:text-gray-300">
                {currentTenant?.name || "Select Tenant"}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${showTenantMenu ? "rotate-180" : ""}`}
              />
            </button>

            {showTenantMenu && (
              <div className="absolute left-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 max-h-64 overflow-y-auto">
                {tenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => {
                      switchTenant(tenant.id);
                      setShowTenantMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      currentTenant?.id === tenant.id
                        ? "bg-blue-50 dark:bg-blue-900/30"
                        : ""
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                      style={{
                        backgroundColor:
                          tenant.branding?.primary_color || "#2563eb",
                      }}
                    >
                      {tenant.name?.[0]}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-800 dark:text-white">
                        {tenant.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {tenant.plan}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search here..."
            className="pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm w-48 focus:w-64 transition-all duration-200 dark:text-white dark:placeholder-gray-500"
          />
        </div>

        {/* Notifications */}
        <button className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-white dark:border-gray-900"></span>
        </button>

        {/* User Profile with Dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-3 pl-4 border-l border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 -m-2 transition-colors"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
              {user?.name?.[0] || "U"}
            </div>
            <div className="text-sm text-left">
              <p className="font-medium text-gray-800 dark:text-white">
                {user?.name || user?.email || "User"}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">
                {ROLE_DISPLAY_NAMES[user?.role] || user?.role || "User"}
              </p>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${showProfileMenu ? "rotate-180" : ""}`}
            />
          </button>

          {/* Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
              {/* Token Balance */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Token Balance
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {tokenBalance.toLocaleString()}
                  </span>
                </div>
              </div>

              <Link
                to="/settings"
                onClick={() => setShowProfileMenu(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </Link>
              <Link
                to="/subscription"
                onClick={() => setShowProfileMenu(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="text-black text-md ">{currency}</span>
                <span>Subscription</span>
              </Link>
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-700 mt-1">
                <div className="flex items-center gap-2">
                  {darkMode ? (
                    <Moon className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Sun className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {darkMode ? "Dark" : "Light"} mode
                  </span>
                </div>
                <button
                  onClick={toggleDarkMode}
                  className={`relative w-10 h-5 rounded-full transition-colors ${darkMode ? "bg-blue-600" : "bg-gray-300"}`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${darkMode ? "left-5" : "left-0.5"}`}
                  />
                </button>
              </div>
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-700 mt-1">
                <div className="flex items-center gap-2">
                  {/* {darkMode ? <Moon className="w-4 h-4 text-gray-400" /> : <Sun className="w-4 h-4 text-gray-400" />} */}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Change Currency in {currency}{" "}
                  </span>
                </div>
                <button
                  onClick={() =>
                    currency === "$" ? toggleCurrency("₹") : toggleCurrency("$")
                  }
                  className={`relative w-10 h-5 rounded-full transition-colors ${currency ==='$' ? "bg-blue-600" : "bg-gray-300"}`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${currency ==='$' ? "left-5" : "left-0.5"}`}
                  />
                </button>
              </div>
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                <Link
                  to="/subscription"
                  onClick={() => setShowProfileMenu(false)}
                  className="block w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors text-center"
                >
                  Buy Tokens
                </Link>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    logout();
                    navigate("/login");
                  }}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
